import { getReferrerAttribution } from "@outschool/attribution";
import {
  esaUtmCampaigns,
  getFundingProgramFromLandingPagePath,
  US_STATES,
  getFundingProgramProperties,
} from "@outschool/funding-programs-shared";
import { useLookupIP } from "@outschool/iplookup-client";
import { onboardingQuizPath } from "@outschool/routes";
import { useLocalStorageReference } from "@outschool/local-storage";
import { useSessionStorageState } from "@outschool/session-storage";
import { useSession } from "./SessionProvider";
import { SelfElectedStateFundingProgram } from "../lib/useSelfElectedStateFundingProgram";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useLocation } from "react-router-dom";
import _ from "lodash";
import { useUpdateCurrentUserPublicFundingMutation } from "../graphql/UpdateCurrentUserPublicFundingMutation";

type EsaExperienceContextValue = {
  isEsaExperience: boolean;
  disablePFBanner: boolean;
  sessionStateFundingProgram: SelfElectedStateFundingProgram | null;
  createAccountVariables: () => SelfElectedStateFundingProgram | undefined;
};

const EsaExperienceContext = createContext<EsaExperienceContextValue>({
  isEsaExperience: false,
  disablePFBanner: false,
  sessionStateFundingProgram: null,
  createAccountVariables: () => undefined,
});

export function EsaExperienceProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { isInUSA } = useLookupIP();
  const location = useLocation();
  const referrerAttribution = getReferrerAttribution();
  const { isEsaSession, currentUser } = useSession();
  const { localStorage } = useLocalStorageReference();
  const [updateCurrentUserPublicFunding] =
    useUpdateCurrentUserPublicFundingMutation();
  const [sessionStateFundingProgram, setSessionStateFundingProgram] =
    useSessionStorageState<SelfElectedStateFundingProgram | null>(
      "sessionStateFundingProgram",
      null,
    );
  const isOnboardingQuizPage = location.pathname === onboardingQuizPath();

  const getSelfElectedStateFundingProgram = useCallback(() => {
    const storedValue = localStorage?.getItem(
      "self-elected-state-funding-program",
    );
    if (storedValue) {
      return JSON.parse(storedValue);
    }
    return null;
  }, [localStorage]);

  const isEsaUtmCampaign = useMemo(() => {
    if (!referrerAttribution || !referrerAttribution.utm_campaign) {
      return false;
    }
    return !!esaUtmCampaigns.find(
      campaign =>
        campaign.toLowerCase() ===
        referrerAttribution.utm_campaign.toLowerCase(),
    );
  }, [referrerAttribution]);

  const fundingProgram = useMemo(() => {
    if (isEsaUtmCampaign) {
      const fundingProgram = getFundingProgramFromLandingPagePath(
        referrerAttribution?.landingPage,
      );
      const isNoStateFundingProgram = ["SUFS", "SFT"].includes(
        referrerAttribution?.utm_campaign.toLowerCase() || "",
      );
      if (fundingProgram) {
        return fundingProgram;
      } else if (!isNoStateFundingProgram && sessionStateFundingProgram) {
        return getFundingProgramProperties(
          sessionStateFundingProgram.stateAbbreviation,
        );
      }
    }
    return null;
  }, [
    isEsaUtmCampaign,
    sessionStateFundingProgram,
    referrerAttribution?.utm_campaign,
    referrerAttribution?.landingPage,
  ]);

  const isEsaExperience = useMemo(() => {
    if (isEsaSession) {
      return true;
    }

    const selfElectedStateFundingProgram = getSelfElectedStateFundingProgram();

    const fundingOptions = sessionStateFundingProgram
      ? sessionStateFundingProgram.fundingOptions
      : selfElectedStateFundingProgram?.fundingOptions;

    const isValidFundingOption = fundingOptions
      ? Object.values(fundingOptions).some(option => option)
      : false;

    if (isValidFundingOption) {
      return true;
    }

    if (
      currentUser &&
      currentUser.selfElectedStateFundingProgram?.stateAbbreviation
    ) {
      return true;
    }

    return isEsaUtmCampaign;
  }, [
    isEsaSession,
    isEsaUtmCampaign,
    sessionStateFundingProgram,
    currentUser,
    getSelfElectedStateFundingProgram,
  ]);

  const disablePFBanner = useMemo(() => {
    if (isEsaExperience) {
      return true;
    }

    if (currentUser) {
      return true;
    }

    if (!isInUSA) {
      return true;
    }

    if (isOnboardingQuizPage) {
      return true;
    }

    const selfElectedStateFundingProgram = getSelfElectedStateFundingProgram();

    return selfElectedStateFundingProgram?.hideBanner || false;
  }, [
    isEsaExperience,
    currentUser,
    isInUSA,
    isOnboardingQuizPage,
    getSelfElectedStateFundingProgram,
  ]);

  useEffect(() => {
    if (isEsaUtmCampaign) {
      const [stateAbbreviation, stateName] = fundingProgram
        ? [
            fundingProgram.abbreviation,
            US_STATES.find(
              state => state.abbreviation === fundingProgram.abbreviation,
            )?.name || "",
          ]
        : ["", ""];
      setSessionStateFundingProgram({
        stateAbbreviation,
        stateName,
        source: referrerAttribution?.utm_campaign.toLowerCase() || "",
        fundingOptions: {
          charter: false,
          statePortal: false,
          otherProgramName: "",
          reimbursement: false,
          selfPay: true,
          stateDebitCard: false,
          stateProgramName: fundingProgram?.programName || "default",
        },
      });
    }
  }, [
    isEsaUtmCampaign,
    referrerAttribution?.utm_campaign,
    setSessionStateFundingProgram,
    fundingProgram,
  ]);

  useEffect(() => {
    const userFundingOption = currentUser?.selfElectedStateFundingProgram
      ?.fundingOptions
      ? Object.values(
          _.omit(
            currentUser.selfElectedStateFundingProgram.fundingOptions,
            "__typename",
          ),
        ).some(fp => fp)
      : false;
    if (currentUser && !userFundingOption && sessionStateFundingProgram) {
      updateCurrentUserPublicFunding({
        variables: {
          details: {
            selfElectedStateFundingProgram: sessionStateFundingProgram,
          },
        },
      });
    }
  }, [sessionStateFundingProgram, currentUser, updateCurrentUserPublicFunding]);

  const createAccountVariables = useCallback(() => {
    if (sessionStateFundingProgram) {
      return sessionStateFundingProgram;
    }

    const selfElectedStateFundingProgram = getSelfElectedStateFundingProgram();

    if (
      selfElectedStateFundingProgram &&
      !selfElectedStateFundingProgram.hasRejectedStateFundingQuestions &&
      selfElectedStateFundingProgram.stateAbbreviation !== ""
    ) {
      return _.omit(
        selfElectedStateFundingProgram,
        "searchFiltersActive",
        "hideBanner",
        "hasRejectedStateFundingQuestions",
      ) as SelfElectedStateFundingProgram;
    }

    return undefined;
  }, [sessionStateFundingProgram, getSelfElectedStateFundingProgram]);

  return (
    <EsaExperienceContext.Provider
      value={{
        isEsaExperience,
        disablePFBanner,
        sessionStateFundingProgram,
        createAccountVariables,
      }}
    >
      {children}
    </EsaExperienceContext.Provider>
  );
}

export function useEsaExperience() {
  const context = useContext(EsaExperienceContext);

  if (context === undefined) {
    throw new Error(
      "useEsaExperience must be used within an EsaExperienceProvider",
    );
  }

  return context;
}
